import {useState, useCallback} from "react";
import {
  DragStartEvent,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {arrayMove} from "@dnd-kit/sortable";

interface UseSortableEditModeProps<T extends {id: string}> {
  items: T[];
  onReorder: (itemIds: string[]) => void;
}

interface UseSortableEditModeReturn<T extends {id: string}> {
  isEditMode: boolean;
  isDragging: boolean;
  activeId: string | null;
  activeItem: T | undefined;
  items: T[];
  toggleEditMode: () => void;
  setEditMode: (enabled: boolean) => void;
  sensors: ReturnType<typeof useSensors>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
}

export function useSortableEditMode<T extends {id: string}>({
  items,
  onReorder,
}: UseSortableEditModeProps<T>): UseSortableEditModeReturn<T> {
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure sensors with iOS-like activation constraints
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms press required (iOS-like long press)
        tolerance: 5, // 5px movement tolerance during delay
      },
    }),
    useSensor(KeyboardSensor) // Accessibility: arrow keys support
  );

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => !prev);
    // Clear any active drag when exiting edit mode
    setActiveId(null);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active, over} = event;
      setActiveId(null);

      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      // Compute new order and trigger mutation
      const reordered = arrayMove(items, oldIndex, newIndex);
      onReorder(reordered.map((item) => item.id));
    },
    [items, onReorder]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const activeItem = items.find((item) => item.id === activeId);

  return {
    isEditMode,
    isDragging: activeId !== null,
    activeId,
    activeItem,
    items,
    toggleEditMode,
    setEditMode: setIsEditMode,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
}
